import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, TrendingUp, Users, Award, Rocket, ShoppingBag, GraduationCap, Car, Briefcase, Heart, LineChart, Bot } from "lucide-react";
import Navbar from "../components/Navbar";

const timelineData = [
  {
    year: "2022",
    title: "Founding & Product Genesis",
    content: "Astergo was founded in 2022 with a mission to build innovative software solutions that empower businesses through technology. We successfully launched our flagship product, laying the groundwork for our future.",
    icon: "/assets/img/appstore.png",
    color: "blue",
    products: [],
  },
  {
    year: "2023",
    title: "Strategic Sector Expansion",
    content: "We launched VidyarthiIndia — an online education platform focused on delivering affordable, high-quality learning with live interactive classes, doubt-solving sessions, and multilingual course support.",
    icon: "/assets/img/VI.jpeg",
    color: "green",
    products: [
      { 
        name: "VidyarthiIndia", 
        logo: "/assets/img/VI.jpeg",
        description: "Online Education Platform",
        playStoreLink: "https://play.google.com/store/apps/details?id=com.vidyarthiindia&hl=en_IN"
      },
    ],
  },
  {
    year: "2024",
    title: "National Recognition & Innovation",
    content: "Our commitment to innovation was recognized nationally with prestigious awards. We launched EasyRides — an intuitive online cab booking app with real-time tracking, fare transparency, and secure payments.",
    icon: "/assets/img/EasyRideLogo.png",
    color: "orange",
    products: [
      { 
        name: "EasyRides", 
        logo: "/assets/img/EasyRideLogo.png",
        description: "Intuitive online cab booking app with real-time tracking, fare transparency, and secure payments",
      },
    ],
  },
  {
    year: "2025",
    title: "Expanding Horizons with SaaS Innovation",
    content: "We launched a comprehensive suite of SaaS-based platforms to empower industries across sectors with cutting-edge technology solutions.",
    icon: "https://placehold.co/150x150/F3F9ED/5B8C5A?text=SaaS",
    color: "purple",
    products: [
     { 
        name: "TBill", 
        logo: "/assets/img/TBillLogo.png",
        description: "Smart billing and invoice platform with GST compliance, inventory management, and multi-store support",
        playStoreLink: "https://play.google.com/store/search?q=Tbill&c=apps&hl=en_IN"
      },
      { 
        name: "RetailMitra", 
        logo: "/assets/img/RetailMitraLogo.png",
        description: "All-in-one retail management app with POS, inventory, customer loyalty, and real-time analytics",
        playStoreLink: "https://play.google.com/store/apps/details?id=com.retailmitra&hl=en_IN"
      },
    ],
  },
  {
    year: "2026",
    title: "AI-Powered Trading & Automation",
    content: "Pioneering the future of automated trading and intelligent business automation with cutting-edge AI technology. We're revolutionizing how businesses trade and communicate with customers.",
    icon: "https://placehold.co/150x150/E0E7FF/6366F1?text=AI",
    color: "indigo",
    products: [
      { 
        name: "TradeLink", 
        icon: LineChart,
        description: "Advanced algorithmic trading platform with automated trade execution based on TradingView signals and custom strategies",
        status: "Live"
      },
      { 
        name: "AgenticAI", 
        icon: Bot,
        description: "AI-powered business automation for WhatsApp & Telegram with intelligent customer query handling and automated responses",
        status: "In Development"
      },
    ],
  },
];

const teamMembers = [
  {
    name: "Bibek Das",
    role: "Founder & CEO",
    image: "https://placehold.co/200x200/A3B18A/FFFFFF?text=Bibek",
    social: {
      linkedin: "#",
      twitter: "#",
      github: "#"
    },
    isFounder: true,
  },
  {
    name: "Deepak Choudhury",
    role: "Chief Technology Officer",
    image: "https://placehold.co/200x200/B2D3C2/FFFFFF?text=Deepak",
    social: {
      linkedin: "#",
      twitter: "#",
      github: "#"
    },
    isFounder: true,
  },
  {
    name: "Rahul Kumar",
    role: "Senior Software Engineer",
    image: "https://placehold.co/200x200/CFB8D8/FFFFFF?text=Rahul",
    social: {
      linkedin: "#",
      twitter: "#",
      github: "#"
    },
  },
  {
    name: "Pankaj Kumar",
    role: "Senior Solutions Architect",
    image: "https://placehold.co/200x200/F1DDCF/FFFFFF?text=Pankaj",
    social: {
      linkedin: "#",
      twitter: "#",
      github: "#"
    },
  },
];

export default function AboutUs() {
  const [activeYear, setActiveYear] = useState("2022");

  const colorClasses = {
    blue: {
      gradient: "from-red-600 to-red-700",
      bg: "bg-red-50",
      text: "text-red-700",
      ring: "ring-red-500/20",
      border: "border-red-500",
      shadow: "shadow-red-500/50",
    },
    green: {
      gradient: "from-red-700 to-red-800",
      bg: "bg-red-50",
      text: "text-red-700",
      ring: "ring-red-500/20",
      border: "border-red-600",
      shadow: "shadow-red-600/50",
    },
    orange: {
      gradient: "from-red-600 to-red-800",
      bg: "bg-red-50",
      text: "text-red-700",
      ring: "ring-red-500/20",
      border: "border-red-500",
      shadow: "shadow-red-500/50",
    },
    purple: {
      gradient: "from-red-700 to-red-900",
      bg: "bg-red-50",
      text: "text-red-800",
      ring: "ring-red-600/20",
      border: "border-red-700",
      shadow: "shadow-red-700/50",
    },
    indigo: {
      gradient: "from-red-800 to-red-950",
      bg: "bg-red-50",
      text: "text-red-900",
      ring: "ring-red-700/20",
      border: "border-red-800",
      shadow: "shadow-red-800/50",
    },
  };

  const activeData = timelineData.find((item) => item.year === activeYear);
  const activeColors = colorClasses[activeData?.color || "blue"];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;900&family=JetBrains+Mono:wght@400;500&display=swap');
        
        .font-display {
          font-family: 'Inter', sans-serif;
          font-weight: 900;
        }
        
        .font-mono-body {
          font-family: 'JetBrains Mono', monospace;
          font-weight: 400;
        }
        
        .font-heading {
          font-family: 'Inter', sans-serif;
          font-weight: 700;
        }

        .timeline-item {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        @media (hover: hover) {
          .timeline-item:hover {
            transform: translateY(-2px);
          }

          .team-card:hover {
            transform: translateY(-8px);
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
          }
        }

        @media (max-width: 768px) {
          .timeline-item:active {
            transform: scale(0.98);
          }

          .team-card:active {
            transform: scale(0.98);
          }
        }

        /* Mobile-specific optimizations */
        @media (max-width: 640px) {
          .font-display {
            font-size: clamp(1.5rem, 8vw, 2.5rem);
          }
        }
      `}</style>

      <Navbar />

      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-red-50">
        {/* Hero Section */}
        <div className="relative py-12 sm:py-16 md:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
          {/* Background elements */}
          <div className="absolute top-10 sm:top-20 right-5 sm:right-20 w-48 h-48 sm:w-72 sm:h-72 lg:w-96 lg:h-96 bg-red-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
          <div className="absolute bottom-10 sm:bottom-20 left-5 sm:left-20 w-48 h-48 sm:w-72 sm:h-72 lg:w-96 lg:h-96 bg-red-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: "2s" }}></div>

          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-block mb-4 sm:mb-6">
                <span className="inline-flex items-center px-3 sm:px-4 py-2 rounded-full bg-red-50 text-red-700 text-xs sm:text-sm font-heading border border-red-200">
                  <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                  About Astergo
                </span>
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-display text-gray-900 mb-4 sm:mb-6 leading-tight px-4">
                Building the Future,{" "}
                <span className="bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
                  One Innovation at a Time
                </span>
              </h1>

              <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-8 sm:mb-12 font-mono-body px-4 leading-relaxed">
                Astergo is a dynamic software development company specializing in cutting-edge SaaS solutions, mobile applications, and custom software that drives business transformation.
              </p>

              {/* Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 max-w-4xl mx-auto">
                {[
                  { icon: Rocket, value: "5+", label: "Products Launched" },
                  { icon: Users, value: "10K+", label: "Active Users" },
                  { icon: Award, value: "15+", label: "Awards Won" },
                  { icon: TrendingUp, value: "200%", label: "Growth Rate" },
                ].map((stat, index) => (
                  <motion.div
                    key={index}
                    className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-100"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    <stat.icon className="w-6 h-6 sm:w-8 sm:h-8 text-red-700 mx-auto mb-2 sm:mb-3" />
                    <div className="text-2xl sm:text-3xl lg:text-4xl font-display text-gray-900">
                      {stat.value}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600 font-mono-body mt-1">
                      {stat.label}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Timeline Section */}
        <div className="relative py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              className="text-center mb-8 sm:mb-12 md:mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-block mb-4 sm:mb-6">
                <span className="inline-flex items-center px-3 sm:px-4 py-2 rounded-full bg-red-50 text-red-700 text-xs sm:text-sm font-heading border border-red-200">
                  <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                  Our Journey
                </span>
              </div>

              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display text-gray-900 mb-3 sm:mb-4 px-4">
                Evolution of{" "}
                <span className="bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
                  Excellence
                </span>
              </h2>

              <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto font-mono-body px-4">
                A timeline of milestones, innovations, and groundbreaking achievements
              </p>
            </motion.div>

            {/* Timeline Navigation */}
            <div className="mb-8 sm:mb-12 overflow-x-auto pb-4 -mx-4 px-4">
              <div className="flex gap-2 sm:gap-3 min-w-max sm:min-w-0 sm:justify-center">
                {timelineData.map((item) => {
                  const colors = colorClasses[item.color];
                  return (
                    <button
                      key={item.year}
                      onClick={() => setActiveYear(item.year)}
                      className={`timeline-item px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-heading text-sm sm:text-base transition-all duration-300 whitespace-nowrap ${
                        activeYear === item.year
                          ? `bg-gradient-to-r ${colors.gradient} text-white shadow-lg ${colors.shadow}`
                          : `bg-white text-gray-700 border-2 ${colors.border} hover:${colors.bg}`
                      }`}
                    >
                      {item.year}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Timeline Content */}
            <div className="relative">
              <AnimatePresence mode="wait">
                {activeData && (
                  <motion.div
                    key={activeYear}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.5 }}
                  >
                    <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
                      <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
                        {/* Left Side - Year & Icon */}
                        <div className={`lg:col-span-4 bg-gradient-to-br ${activeColors.gradient} p-6 sm:p-8 lg:p-12 text-white flex flex-col items-center justify-center text-center`}>
                          <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            className="mb-4 sm:mb-6"
                          >
                            <div className="w-24 h-24 sm:w-32 sm:h-32 lg:w-40 lg:h-40 rounded-full bg-white/20 backdrop-blur-sm p-4 sm:p-6 lg:p-8 mx-auto border-4 border-white/30">
                              <img
                                src={activeData.icon}
                                alt={activeData.year}
                                className="w-full h-full object-contain"
                              />
                            </div>
                          </motion.div>
                          <div className="text-5xl sm:text-6xl lg:text-7xl font-display mb-2 sm:mb-4">
                            {activeData.year}
                          </div>
                          <div className="text-base sm:text-lg lg:text-xl font-heading opacity-90">
                            {activeData.title}
                          </div>
                        </div>

                        {/* Right Side - Content */}
                        <div className="lg:col-span-8 p-6 sm:p-8 lg:p-12">
                          <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-6 sm:mb-8 font-mono-body">
                            {activeData.content}
                          </p>

                          {activeData.products.length > 0 && (
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.5, delay: 0.3 }}
                            >
                              <h3 className="text-lg sm:text-xl font-heading text-gray-900 mb-4 sm:mb-6 flex items-center gap-2">
                                <Rocket className="w-5 h-5 sm:w-6 sm:h-6 text-red-700" />
                                Products Launched
                              </h3>
                              <div className="space-y-4 sm:space-y-6">
                                {activeData.products.map((product, index) => {
                                  const ProductIcon = product.icon;
                                  return (
                                    <motion.div
                                      key={index}
                                      className={`${activeColors.bg} border-2 ${activeColors.border} rounded-xl sm:rounded-2xl p-4 sm:p-6 flex items-start gap-3 sm:gap-4 hover:shadow-lg transition-shadow duration-300`}
                                      initial={{ opacity: 0, x: -20 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      transition={{ duration: 0.4, delay: 0.1 * index }}
                                    >
                                      {product.logo ? (
                                        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg overflow-hidden shadow-md flex-shrink-0">
                                          <img src={product.logo} alt={product.name} className="w-full h-full object-cover" />
                                        </div>
                                      ) : (
                                        <div className={`${activeColors.text} bg-white p-2.5 sm:p-3 rounded-lg shadow-md flex-shrink-0`}>
                                          <ProductIcon className="w-6 h-6 sm:w-7 sm:h-7" />
                                        </div>
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                                          <div className="font-heading text-gray-900 text-base sm:text-lg">
                                            {product.name}
                                          </div>
                                          {product.status && (
                                            <span className={`text-[10px] sm:text-xs px-2 sm:px-2.5 py-0.5 rounded-full font-heading ${
                                              product.status === 'Live' 
                                                ? 'bg-green-100 text-green-700 border border-green-300' 
                                                : 'bg-yellow-100 text-yellow-700 border border-yellow-300'
                                            }`}>
                                              {product.status}
                                            </span>
                                          )}
                                        </div>
                                        <div className="text-gray-600 text-xs sm:text-sm leading-relaxed mb-2 sm:mb-3 font-mono-body">
                                          {product.description}
                                        </div>
                                        {product.playStoreLink && (
                                          <a
                                            href={product.playStoreLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-heading text-red-700 hover:text-red-900 transition-colors"
                                          >
                                            <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 24 24">
                                              <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                                            </svg>
                                            <span>View on Play Store</span>
                                          </a>
                                        )}
                                      </div>
                                    </motion.div>
                                  );
                                })}
                              </div>
                            </motion.div>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Team Section */}
        <div className="relative py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
          {/* Background elements */}
          <div className="absolute top-10 sm:top-20 right-5 sm:right-20 w-48 h-48 sm:w-72 sm:h-72 lg:w-96 lg:h-96 bg-red-200 rounded-full mix-blend-multiply filter blur-3xl opacity-15 animate-pulse"></div>
          <div className="absolute bottom-10 sm:bottom-20 left-5 sm:left-20 w-48 h-48 sm:w-72 sm:h-72 lg:w-96 lg:h-96 bg-red-300 rounded-full mix-blend-multiply filter blur-3xl opacity-15 animate-pulse" style={{ animationDelay: '2s' }}></div>

          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              className="text-center mb-8 sm:mb-12 md:mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-block mb-4 sm:mb-6">
                <span className="inline-flex items-center px-3 sm:px-4 py-2 rounded-full bg-red-50 text-red-700 text-xs sm:text-sm font-heading border border-red-200">
                  <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                  Leadership
                </span>
              </div>

              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display text-gray-900 mb-3 sm:mb-4 px-4">
                Meet Our{" "}
                <span className="bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
                  Exceptional Team
                </span>
              </h2>

              <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto font-mono-body px-4">
                Visionary leaders and talented professionals driving innovation and excellence
              </p>
            </motion.div>

            {/* Team Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {teamMembers.map((member, index) => (
                <motion.div
                  key={index}
                  className="team-card group bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-xl border border-gray-100 relative backdrop-blur-sm bg-opacity-90"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  {member.isFounder && (
                    <div className="absolute -top-2 sm:-top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-400 to-amber-500 text-white text-[10px] sm:text-xs font-heading px-3 sm:px-4 py-1 sm:py-1.5 rounded-full shadow-lg z-10 uppercase tracking-wider">
                      Founder
                    </div>
                  )}

                  <div className="w-24 h-24 sm:w-32 sm:h-32 mx-auto rounded-full overflow-hidden mb-4 sm:mb-6 border-4 border-gray-100 group-hover:border-red-700 transition-all duration-300 shadow-lg">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                    />
                  </div>

                  <h3 className="text-lg sm:text-xl font-heading text-gray-900 text-center mb-1 sm:mb-2">
                    {member.name}
                  </h3>
                  <p className="text-red-700 text-xs sm:text-sm font-mono-body text-center mb-4 sm:mb-6">
                    {member.role}
                  </p>

                  {/* Social Links */}
                  <div className="flex justify-center gap-2 sm:gap-3 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {member.social.linkedin && (
                      <a
                        href={member.social.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 sm:p-2.5 bg-gray-100 rounded-lg hover:bg-red-700 hover:text-white transition-all duration-300"
                      >
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                        </svg>
                      </a>
                    )}
                    {member.social.twitter && (
                      <a
                        href={member.social.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 sm:p-2.5 bg-gray-100 rounded-lg hover:bg-red-700 hover:text-white transition-all duration-300"
                      >
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8.297 21H.482l6.23-7.234L0 3h8.375L12 9.771 18.625 3H24l-6.84 7.971L24 21h-8.084L12 14.229 5.375 21H.482zM15.4 4.5h2.1L8.76 19.5h-2.1L15.4 4.5z" />
                        </svg>
                      </a>
                    )}
                    {member.social.github && (
                      <a
                        href={member.social.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 sm:p-2.5 bg-gray-100 rounded-lg hover:bg-gray-900 hover:text-white transition-all duration-300"
                      >
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.087-.731.084-.716.084-.716 1.205.082 1.838 1.235 1.838 1.235 1.07 1.835 2.809 1.305 3.492.998.108-.776.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.046.138 3.003.404 2.292-1.552 3.3-1.23 3.3-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.559 21.782 24 17.218 24 12c0-6.627-5.373-12-12-12z" />
                        </svg>
                      </a>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}